import React, { PropsWithChildren, ReactNode, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { Icon, InputContainer, InputElement, InputStyleProps, InputWrapper, Tooltip } from '@column/column-ui-kit';

import { copyToClipboard } from '~/util';

export interface CopyInputProps {
  className?: string;
  value?: string;
  placeholder?: string;
  icon?: ReactNode;
  disabled?: boolean;
  showValueHover?: boolean;
  size?: InputStyleProps['size'];
  isMonospaced?: boolean;
  variant?: 'text-only';
  fullWidth?: boolean;
}

const StyledInputElement = styled(InputElement)`
  mask-image: linear-gradient(90deg, rgb(0, 0, 0) 93%, transparent 100%);
`;

const StyledInputContainer = styled(InputContainer)`
  display: flex;
`;

export const Copy = styled.button<{ isEmpty: boolean }>`
  appearance: none;
  border: none;
  background: transparent;
  margin: 0;
  cursor: pointer;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  opacity: 0;
  transition:
    opacity 0.2s,
    transform 0.2s;
  border-radius: 6px;
  padding: 4px;

  svg {
    --icon-color: ${({ theme }) => theme.secondary.blendToBackground(500)};
    --icon-size: 16px;

    path {
      transition:
        fill 0.2s,
        stroke 0.2s;
    }
  }

  ${({ isEmpty }) =>
    isEmpty &&
    css`
      pointer-events: none;
      svg {
        --icon-color: ${({ theme }) => theme.secondary.blendToBackground(300)};
      }
    `}

  &:hover {
    background: ${({ theme }) => theme.secondary.blendToBackground(60)};

    svg {
      --icon-color: ${({ theme }) => theme.secondary.blendToBackground(800)};
    }
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.primary.blendToBackground(350)};
    outline-offset: 1px;
  }
`;

const Spacer = styled.div`
  width: 0;
  transition: width 0.2s ease-in-out;
  flex-shrink: 0;
`;

const StyledInputWrapper = styled(InputWrapper)<{ $variant?: 'text-only'; $fullWidth?: boolean }>`
  padding-right: 12px;
  position: relative;
  --input-background: transparent;
  box-shadow:
    inset 0 0 0 var(--input-border, 1px) var(--input-border-color, #2b314326),
    0 0 0 var(--input-outline, 0px) var(--input-outline-color, transparent);

  &:hover {
    ${Copy} {
      opacity: 1;
      transform: translateY(-50%) scale(1);
    }
    ${Spacer} {
      width: 24px;
    }
  }

  ${({ size }) =>
    size === 'small' &&
    css`
      padding: 8px 8px 8px 12px;
    `}

  ${({ $variant }) =>
    $variant === 'text-only' &&
    css`
      padding: 0;
      box-shadow: none;
      border-radius: 0;
      --input-background: transparent;
    `}

  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      width: 100%;
    `}
`;

export const CopyInput: React.FC<PropsWithChildren<CopyInputProps>> = (props) => {
  const [isTooltipShow, setIsTooltipShow] = useState<boolean>(false);
  const [showCopied, setShowCopied] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleCopyClick = () => {
    setIsTooltipShow(true);
    setShowCopied(true);
    copyToClipboard(props.value ?? '');
    setTimeout(() => {
      setIsTooltipShow(false);
      setTimeout(() => setShowCopied(false), 200);
    }, 550);
  };

  const handleFocus = () => {
    inputRef.current?.select();
  };

  return (
    <StyledInputWrapper
      className={props.className}
      size={props.size}
      variant="subtle"
      $variant={props.variant}
      $fullWidth={props.fullWidth}
    >
      <StyledInputContainer>
        <StyledInputElement
          ref={inputRef}
          value={props.value ?? ''}
          type="text"
          autoComplete="off"
          readOnly
          placeholder={props.placeholder}
          disabled={props.disabled}
          $isMonospaced={props.isMonospaced}
          onFocus={handleFocus}
        />
        <Spacer />
        <Copy onClick={handleCopyClick} isEmpty={!props.value} type="button" aria-label="Copy value">
          <Tooltip
            content={showCopied ? 'Copied!' : (props.value ?? '')}
            maxWidth="280px"
            zIndex={100}
            isOpen={props.showValueHover ? undefined : isTooltipShow}
          >
            {props.icon ?? <Icon.Copy />}
          </Tooltip>
        </Copy>
      </StyledInputContainer>
      {props.children}
    </StyledInputWrapper>
  );
};
